import { adaptiveGeodesicPath } from './distance-math.js';
import { getArrowHeadLength, getRenderedMarkerLabelOffset, getRenderedTextFontSize, getRenderedWidth, isMarkerLabelStroke } from './map-math.js';
import { drawMapMarker, getMarkerScreenBounds } from './marker-renderer.js';
import { getPolygonFillOpacity, getPolygonOutlineOpacity, getStrokeLineStyle, getStrokeOpacity } from './stroke-style.js';
import type { Point, Stroke } from '../types/app.js';
import { isLineOrArrowTool, linePathPoints } from './line-geometry.js';

export interface ScreenPoint { x: number; y: number }

/** Draw complete path copies wherever they intersect the visible map. */
function worldWrapOffsets(points: ScreenPoint[], options: StrokeRenderOptions, crossesAntimeridian: boolean, padding: number): ScreenPoint[] {
  const { worldOffset, viewport } = options;
  if (!worldOffset) return [{ x: 0, y: 0 }];
  if (!viewport) return (crossesAntimeridian ? [-1, 0, 1] : [0]).map((copy) => ({ x: copy * worldOffset.x, y: copy * worldOffset.y }));
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  }
  const horizontal = Math.abs(worldOffset.x) >= Math.abs(worldOffset.y);
  const step = horizontal ? worldOffset.x : worldOffset.y;
  const lower = (-padding - (horizontal ? maxX : maxY)) / step;
  const upper = ((horizontal ? viewport.x - minX : viewport.y - minY) + padding) / step;
  const first = Math.ceil(Math.min(lower, upper));
  const last = Math.floor(Math.max(lower, upper));
  const offsets: ScreenPoint[] = [];
  for (let copy = first; copy <= last; copy += 1) {
    const offset = { x: copy * worldOffset.x, y: copy * worldOffset.y };
    if (maxX + offset.x >= -padding && minX + offset.x <= viewport.x + padding
      && maxY + offset.y >= -padding && minY + offset.y <= viewport.y + padding) offsets.push(offset);
  }
  return offsets;
}

function continuousScreenPath(points: ScreenPoint[], worldOffset: ScreenPoint | undefined): ScreenPoint[] {
  if (!worldOffset) return points;
  const lengthSquared = worldOffset.x ** 2 + worldOffset.y ** 2;
  let previous = points[0];
  return points.map((point) => {
    const copies = Math.round(((point.x - previous.x) * worldOffset.x + (point.y - previous.y) * worldOffset.y) / lengthSquared);
    previous = { x: point.x - copies * worldOffset.x, y: point.y - copies * worldOffset.y };
    return previous;
  });
}

/** Traces a path once per offset, so callers can render every wrapped world copy in one `ctx.beginPath()`. */
function moveAndLineWrapped(ctx: CanvasRenderingContext2D, renderedPoints: ScreenPoint[], offsets: ScreenPoint[]): void {
  for (const offset of offsets) {
    const first = renderedPoints[0];
    ctx.moveTo(first.x + offset.x, first.y + offset.y);
    for (let index = 1; index < renderedPoints.length; index += 1) {
      const point = renderedPoints[index];
      ctx.lineTo(point.x + offset.x, point.y + offset.y);
    }
  }
}

function traceClosedPolygon(ctx: CanvasRenderingContext2D, points: ScreenPoint[], offsets: ScreenPoint[]): void {
  for (const offset of offsets) {
    ctx.moveTo(points[0].x + offset.x, points[0].y + offset.y);
    for (let index = 1; index < points.length; index += 1) ctx.lineTo(points[index].x + offset.x, points[index].y + offset.y);
    ctx.closePath();
  }
}

function renderPressurePath(
  ctx: CanvasRenderingContext2D,
  points: ScreenPoint[],
  offsets: ScreenPoint[],
  pressures: number[],
  baseWidth: number,
): void {
  for (let index = 1; index < points.length; index += 1) {
    const pressure = (pressures[index - 1] + pressures[index]) / 2;
    ctx.lineWidth = Math.max(0.5, baseWidth * (0.35 + pressure * 1.3));
    for (const offset of offsets) {
      ctx.beginPath();
      ctx.moveTo(points[index - 1].x + offset.x, points[index - 1].y + offset.y);
      ctx.lineTo(points[index].x + offset.x, points[index].y + offset.y);
      ctx.stroke();
    }
  }
}

function traceFreehandOrPolygon(
  ctx: CanvasRenderingContext2D,
  stroke: Stroke,
  options: StrokeRenderOptions,
  renderedWidth: number,
): boolean {
  const points = stroke.points;
  let previousLng = points[0].lng;
  let crossesAntimeridian = false;
  const unwrappedPoints: Point[] = [points[0]];
  for (let index = 1; index < points.length; index += 1) {
    let lng = points[index].lng;
    const deltaLng = lng - previousLng;
    if (deltaLng > 180) { lng -= 360; crossesAntimeridian = true; }
    else if (deltaLng < -180) { lng += 360; crossesAntimeridian = true; }
    previousLng = lng;
    unwrappedPoints.push({ lat: points[index].lat, lng });
  }
  const renderedPoints = continuousScreenPath(unwrappedPoints.map(options.project), options.worldOffset);
  const offsets = worldWrapOffsets(renderedPoints, options, crossesAntimeridian, renderedWidth);
  if (stroke.tool === 'polygon') {
    traceClosedPolygon(ctx, renderedPoints, offsets);
    const baseAlpha = ctx.globalAlpha;
    const fillOpacity = options.applyStrokeOpacity === false
      ? Math.max(0, Math.min(1, stroke.fillOpacity ?? 0.25))
      : getPolygonFillOpacity(stroke);
    ctx.globalAlpha = baseAlpha * fillOpacity;
    ctx.fill('evenodd');
    ctx.globalAlpha = baseAlpha * (options.applyStrokeOpacity === false ? 1 : getPolygonOutlineOpacity(stroke));
  } else if (stroke.pressures?.length === renderedPoints.length) {
    renderPressurePath(ctx, renderedPoints, offsets, stroke.pressures, renderedWidth);
    return true;
  } else {
    moveAndLineWrapped(ctx, renderedPoints, offsets);
  }
  return false;
}

export interface StrokeRenderOptions {
  project: (point: Point) => ScreenPoint;
  currentZoom: number;
  /** Signed screen displacement between adjacent wrapped worlds. */
  worldOffset?: ScreenPoint;
  viewport?: ScreenPoint;
  applyStrokeOpacity?: boolean;
  skipTextStrokeId?: string | null;
  showMarkerText?: boolean;
}

function wrapMarkerText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    const words = paragraph.trim().split(/\s+/).filter(Boolean).flatMap((word) => (
      ctx.measureText(word).width <= maxWidth ? [word] : word.match(/.{1,32}/g) || []
    ));
    if (!words.length) continue;
    let line = words.shift()!;
    for (const word of words) {
      const next = `${line} ${word}`;
      if (ctx.measureText(next).width <= maxWidth) line = next;
      else { lines.push(line); line = word; }
    }
    lines.push(line);
  }
  return lines;
}

function drawMarkerTextBubble(ctx: CanvasRenderingContext2D, stroke: Stroke, anchor: ScreenPoint, renderedWidth: number): void {
  if (!stroke.text) return;
  const paddingX = 10;
  const paddingY = 7;
  const lineHeight = 17;
  ctx.save();
  ctx.font = '600 13px system-ui, sans-serif';
  const lines = wrapMarkerText(ctx, stroke.text, 240);
  if (!lines.length) { ctx.restore(); return; }
  const textWidth = Math.max(...lines.map((line) => ctx.measureText(line).width));
  const width = textWidth + paddingX * 2;
  const height = lines.length * lineHeight + paddingY * 2;
  const markerBounds = getMarkerScreenBounds(anchor, renderedWidth, stroke.markerIcon);
  const x = anchor.x - width / 2;
  const y = markerBounds.maxY + 8;
  ctx.shadowColor = 'rgba(15, 23, 42, 0.2)';
  ctx.shadowBlur = 10;
  ctx.shadowOffsetY = 3;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, 7);
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.strokeStyle = 'rgba(15, 23, 42, 0.14)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = '#0f172a';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  lines.forEach((line, index) => ctx.fillText(line, anchor.x, y + paddingY + lineHeight * (index + 0.5)));
  ctx.restore();
}

function renderMarker(ctx: CanvasRenderingContext2D, stroke: Stroke, options: StrokeRenderOptions, renderedWidth: number): void {
  const anchor = options.project(stroke.points[0]);
  ctx.save();
  if (options.applyStrokeOpacity !== false) ctx.globalAlpha *= getStrokeOpacity(stroke);
  drawMapMarker(ctx, anchor, stroke.color, renderedWidth, stroke.markerIcon);
  ctx.restore();
  if (options.showMarkerText && stroke.text) drawMarkerTextBubble(ctx, stroke, anchor, renderedWidth);
}

/** Drafts can have fewer points than a completed drawing. */
export function renderStrokeDraft(ctx: CanvasRenderingContext2D, stroke: Stroke, options: StrokeRenderOptions): void {
  if (!stroke.points.length || (stroke.points.length < 2 && !['marker', 'text', 'freehand'].includes(stroke.tool))) return;
  renderStroke(ctx, stroke, options);
}

export function renderStroke(
  ctx: CanvasRenderingContext2D,
  stroke: Stroke,
  options: StrokeRenderOptions,
): void {
  const { points, color, tool = 'freehand' } = stroke;
  if (tool === 'text') {
    if (stroke.id === options.skipTextStrokeId || !stroke.text) return;
    const projectedAnchor = options.project(points[0]);
    const isMarkerLabel = isMarkerLabelStroke(stroke);
    const labelOffset = getRenderedMarkerLabelOffset(stroke, options.currentZoom);
    const anchor = { x: projectedAnchor.x, y: projectedAnchor.y + labelOffset };
    const fontSize = getRenderedTextFontSize(stroke, options.currentZoom);
    const lines = stroke.text.split('\n');
    const lineHeight = fontSize * 1.2;
    ctx.save();
    if (options.applyStrokeOpacity !== false) ctx.globalAlpha *= getStrokeOpacity(stroke);
    ctx.font = `bold ${fontSize}px system-ui, sans-serif`;
    const maxWidth = Math.max(...lines.map((line) => ctx.measureText(line).width));
    if (stroke.rotation) {
      const centerX = isMarkerLabel ? anchor.x : anchor.x + maxWidth / 2;
      const centerY = anchor.y + lines.length * lineHeight / 2;
      ctx.translate(centerX, centerY);
      ctx.rotate(stroke.rotation);
      ctx.translate(-centerX, -centerY);
    }
    ctx.fillStyle = color;
    ctx.textBaseline = 'top';
    ctx.textAlign = isMarkerLabel ? 'center' : 'start';
    lines.forEach((line, index) => ctx.fillText(line, anchor.x, anchor.y + index * lineHeight));
    ctx.restore();
    return;
  }

  const renderedWidth = getRenderedWidth(stroke, options.currentZoom);
  if (tool === 'marker') {
    renderMarker(ctx, stroke, options, renderedWidth);
    return;
  }

  const pathPoints = isLineOrArrowTool(tool) ? linePathPoints(stroke) : points;
  const lastPoint = pathPoints[pathPoints.length - 1];
  ctx.save();
  if (tool !== 'polygon' && options.applyStrokeOpacity !== false) ctx.globalAlpha *= getStrokeOpacity(stroke);
  ctx.strokeStyle = color;
  ctx.fillStyle = stroke.fillColor || color;
  ctx.lineWidth = renderedWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if ((tool === 'line' || tool === 'arrow') && getStrokeLineStyle(stroke) === 'dashed') {
    ctx.setLineDash([Math.max(8, renderedWidth * 2.6), Math.max(7, renderedWidth * 1.8)]);
  }
  ctx.beginPath();

  if (tool === 'freehand' && points.length === 1) {
    const point = options.project(points[0]);
    const pressure = stroke.pressures?.[0] ?? 0.5;
    ctx.fillStyle = color;
    ctx.arc(point.x, point.y, Math.max(0.5, renderedWidth * (0.35 + pressure * 1.3)) / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    return;
  }

  if (tool === 'freehand' || tool === 'polygon') {
    // The helper keeps the curve continuous across wrapped world copies.
    if (traceFreehandOrPolygon(ctx, stroke, options, renderedWidth)) {
      ctx.restore();
      return;
    }
  } else if (tool === 'circle') {
    const first = options.project(points[0]);
    const last = options.project(lastPoint);
    ctx.arc(
      (first.x + last.x) / 2,
      (first.y + last.y) / 2,
      Math.hypot(last.x - first.x, last.y - first.y) / 2,
      0,
      Math.PI * 2,
    );
  } else {
    let finalRenderedPoints: ScreenPoint[] = [];
    let finalOffsets = [{ x: 0, y: 0 }];
    for (let index = 1; index < pathPoints.length; index += 1) {
      const path = stroke.geodesic === false || (isLineOrArrowTool(tool) && pathPoints !== points)
        ? { projected: [options.project(pathPoints[index - 1]), options.project(pathPoints[index])], crossesAntimeridian: false }
        : adaptiveGeodesicPath(pathPoints[index - 1], pathPoints[index], options.project);
      const renderedPoints = continuousScreenPath(path.projected, options.worldOffset);
      const offsets = worldWrapOffsets(renderedPoints, options, path.crossesAntimeridian, getArrowHeadLength(renderedWidth));
      moveAndLineWrapped(ctx, renderedPoints, offsets);
      finalRenderedPoints = renderedPoints;
      finalOffsets = offsets;
    }
    if (tool === 'arrow') {
      ctx.stroke();
      const renderedLast = finalRenderedPoints[finalRenderedPoints.length - 1];
      const penultimate = finalRenderedPoints[finalRenderedPoints.length - 2];
      const angle = Math.atan2(renderedLast.y - penultimate.y, renderedLast.x - penultimate.x);
      const arrowLength = getArrowHeadLength(renderedWidth);
      const arrowAngle = Math.PI / 6;
      ctx.beginPath();
      for (const offset of finalOffsets) {
        ctx.moveTo(renderedLast.x + offset.x, renderedLast.y + offset.y);
        ctx.lineTo(renderedLast.x + offset.x - arrowLength * Math.cos(angle - arrowAngle), renderedLast.y + offset.y - arrowLength * Math.sin(angle - arrowAngle));
        ctx.moveTo(renderedLast.x + offset.x, renderedLast.y + offset.y);
        ctx.lineTo(renderedLast.x + offset.x - arrowLength * Math.cos(angle + arrowAngle), renderedLast.y + offset.y - arrowLength * Math.sin(angle + arrowAngle));
      }
    }
  }
  if (tool !== 'polygon' || options.applyStrokeOpacity === false || getPolygonOutlineOpacity(stroke) > 0) ctx.stroke();
  ctx.restore();
}
